import React, { useEffect, useState } from "react";
import { MdOutlineAdd, MdOutlineDelete, MdOutlineEdit } from "react-icons/md";
import { useNavigate } from "react-router";
import { Breadcrumb, BreadcrumbItem, Modal } from "reactstrap";
import Button from "src/components/Buttons/Button";
import "./rcsa.css";
import { useTranslation } from "react-i18next";
import { CiExport } from "react-icons/ci";
import { CiFilter } from "react-icons/ci";
import Select from 'react-select';
import ExcelJS from 'exceljs';
import { toast } from "react-toastify";
import { getRCSAByPage } from "src/services/question";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { uploadExcelForRCSA } from "src/services/question";
import { TfiImport } from "react-icons/tfi";

export default function RcsaCatalog() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState<number>(10);
  const [pageNo, setPageNo] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [risks, setRisks] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [notAllowed, setNotAllowed] = useState(false);
  // State for quarter and year
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedYear, setSelectedYear] = useState(2024);


  // Options for quarter and year
  const quarterOptions = [
    { value: 'Q1', label: 'Q1' },
    { value: 'Q2', label: 'Q2' },
    { value: 'Q3', label: 'Q3' },
    { value: 'Q4', label: 'Q4' },
  ];

  const yearOptions: any = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year, label: year.toString() };
  });
  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Bank`s Self Assessment');
  
    // Fetch data using the same logic as the table
    const response = await getRCSAByPage(selectedQuarter, selectedYear, 0, 500);
  
    if (response.success) {
      const rawData = response?.data?.data;
      const groupedData: any = {};
  
      // Group data by question (same logic as filterData)
      rawData.forEach((item: any) => {
        if (!item.question) return; // Skip items without a question
  
        const questionText = item.question.question_en || "N/A";
  
        // Initialize the question group if it doesn't exist
        if (!groupedData[questionText]) {
          groupedData[questionText] = {
            function: "N/A",
            domain: "N/A",
            question: questionText,
            bankResponse: item.answer || "N/A",
            controlType: "N/A",
            examples: "N/A",
            controlMapping: "N/A",
            complianceBrief: "N/A",
            impactedArea: "N/A",
            owner: "Test"
          };
        }
  
        // Process controls for the question
        if (item.question.controls && item.question.controls.length > 0) {
          item.question.controls.forEach((control: any) => {
            const functionName = control.control?.ControlDomains[0]?.domain?.parent?.en_name || "N/A";
            const domain = control.control?.ControlDomains[0]?.domain?.en_name || "N/A";
            const controlMapping = control.control?.description || "N/A";
            const evidence = control.control?.objective || "N/A";
            
            const controlType = control.control?.keyControl == false ? "Advisory" : control.control?.keyControl == true ? "Mandatory" : "N/A";
  
            // Skip if complianceControlActions is empty
            if (control.control?.complianceControlActions?.length === 0) {
              //console.log("complianceControlActions is empty");
              return;
            }
  
            // Extract justification
            const complianceBrief = control.control?.complianceControlActions[0]?.justification ?? "N/A";
            const user = control.control?.complianceControlActions[0]?.manager?.name || "N/A";
            const impactedArea = control.control?.complianceControlActions[0]?.surveyImpactedAreas
              ?.map((area: any) => area.impactedArea.name).join(", ") || "N/A";
  
            // Update the question group with control details
            groupedData[questionText].function = functionName;
            groupedData[questionText].domain = domain;
            groupedData[questionText].controlType = controlType;
            groupedData[questionText].controlMapping = controlMapping;
            groupedData[questionText].evidence = evidence;
            groupedData[questionText].complianceBrief = complianceBrief;
            groupedData[questionText].impactedArea = impactedArea;
            groupedData[questionText].owner = user;
          });
        }
      });
  
      // Convert the grouped data into an array
      const dataToExport = Object.values(groupedData);
  
      // Add headers with styling
      worksheet.columns = [
        { header: t("Function") as string, key: 'function', width: 30 },
        { header: t("Domain") as string, key: 'domain', width: 30 },
        { header: t("Question") as string, key: 'question', width: 60 },
        { header: t("Bank Response") as string, key: 'bankResponse', width: 15 },
        { header: t("Control Type") as string, key: 'controlType', width: 15 },
        { header: t("Examples of Required Samples") as string, key: 'evidence', width: 40 },
        { header: t("Control Mapping") as string, key: 'controlMapping', width: 50 },
        { header: t("Bank Compliance Brief / Justification") as string, key: 'complianceBrief', width: 50 },
        { header: t("Impacted/Affected Area") as string, key: 'impactedArea', width: 25 },
        { header: t("Owner") as string, key: 'owner', width: 20 },
      ];
  
      // Set header row height
      worksheet.getRow(1).height = 30;
  
      // Style the header row
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '000000' }, // Black background
        };
        cell.font = {
          color: { argb: 'FFFFFF' }, // White text
          bold: true,
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
  
      // Add data rows and style the columns
      dataToExport.forEach((row: any) => {
        const newRow = worksheet.addRow(row);
  
        // Style the "Function" column (1st column)
        const functionCell = newRow.getCell('function');
        functionCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD0B162' }, // Light gray for Function
        };
        functionCell.font = {
          color: { argb: '000000' }, // Black text
        };
        functionCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        functionCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
  
        // Style the "Domain" column (2nd column)
        const domainCell = newRow.getCell('domain');
        domainCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF2E9D2' }, // Slightly darker gray for Domain
        };
        domainCell.font = {
          color: { argb: '000000' }, // Black text
        };
        domainCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
        domainCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
  
        // Style the "Control Type" column (5th column)
        const controlTypeCell = newRow.getCell('controlType');
        controlTypeCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: row.controlType == 'Mandatory' ? 'FFDC4A4A' : row.controlType == "Advisory" ? 'FFE8C412':'FFFFFFFF' }, // Dynamic color
        };
        controlTypeCell.font = {
          color: { argb: row.controlType == 'Advisory' ? 'FF000000' : 'FFFFFFFF' }, // Dynamic color
        };
        controlTypeCell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        controlTypeCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
  
      // Save the file
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, "Bank's Self Assessment.xlsx");
      });
    } else {
      toast.error(response.message);
    }
  };

  // Custom styles for react-select to reduce height
  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: '50px',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      height: '40px',
      padding: '0 6px',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: '40px',
    }),
  };

  const handlePagination = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPageNo(newPage);
    }
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    filterData();
  }, [selectedQuarter, selectedYear, page, pageSize]);

  const filterData = async () => {
    const response = await getRCSAByPage(selectedQuarter, selectedYear, page, pageSize);

    if (response.success) {
      let rawData = response?.data?.data
      setTotalPages(response?.data?.totalPages)
      const groupedData: any = {};
      // Group data by question
      rawData.forEach((item: any) => {
        if (!item.question) return; // Skip items without a question

        const questionText = item.question.question_en || "N/A";

        // Initialize the question group if it doesn't exist
        if (!groupedData[questionText]) {
          groupedData[questionText] = {
            function: "N/A",
            domain: "N/A",
            question: questionText,
            bankResponse: item.answer || "N/A",
            controlType: "N/A",
            examples: "N/A",
            controlMapping: "N/A",
            complianceBrief: "N/A",
            impactedArea: "N/A",
            owner: "Test"
          };
        }

        // Process controls for the question
        if (item.question.controls && item.question.controls.length > 0) {
          item.question.controls.forEach((control: any) => {
            const functionName = control.control?.ControlDomains[0]?.domain?.parent?.en_name || "N/A";
            const domain = control.control?.ControlDomains[0]?.domain?.en_name || "N/A";
            const controlMapping = control.control?.description || "N/A";
            const evidence = control.control?.objective || "N/A";
            const controlType = control.control?.keyControl == false ? "Advisory" : control.control?.keyControl == true ? "Mandatory" : "N/A";

            // Skip if complianceControlActions is empty
            if (control.control?.complianceControlActions?.length === 0) {
              //console.log("complianceControlActions is empty");
              return;
            }

            // Extract justification
            const complianceBrief = control.control?.complianceControlActions[0]?.justification ?? "N/A";

            const user = control.control?.complianceControlActions[0]?.manager?.name || "N/A";

            const impactedArea = control.control?.complianceControlActions[0]?.surveyImpactedAreas
              ?.map((area: any) => area.impactedArea.name).join(", ") || "N/A";

            // Update the question group with control details
            groupedData[questionText].function = functionName;
            groupedData[questionText].domain = domain;
            groupedData[questionText].controlType = controlType;
            groupedData[questionText].controlMapping = controlMapping;
            groupedData[questionText].evidence = evidence;
            groupedData[questionText].complianceBrief = complianceBrief;
            groupedData[questionText].impactedArea = impactedArea;
            groupedData[questionText].owner = user;
          });
        }
      });

      // Convert the grouped data into an array
      let newarr = Object.values(groupedData);

      setData(newarr);
      setTotalPages(response?.data?.totalPages);
    }
    else {
      toast.error(response.message);
    }
  };


  const handleDownloadTemplate = () => {
    const rcsaData = [
      [
        "Survey Name", 
        "Category", 
        "Reference", 
        "Question", 
        "Control Mapping", 
        "Bank Response", 
        "Control Type",
        "Quarter", 
        "Year", 
        "Due Date", 
        "Domain", 
        "Bank Compliance Brief Description/Justifications", 
        "Manager", 
        "Implementer", 
        "Impacted/Affected Area", 
        "answerdby"
      ],
      // Example row
      [
        "Survey 1", 
        "Category 1", 
        "Reference 1", 
        "Question 1", 
        "Control 1", 
        "Fully Implemented", 
        "Mandatory",
        "Q1", 
        "2024", 
        "2024-03-31", 
        "Domain 1", 
        "Description 1", 
        "Manager Name", 
        "Implementer Name", 
        "Area 1, Area 2", 
        "User Name"
      ],
      // Add more rows as needed
    ];
  
    // Create a new workbook and add sheets
    const workbook = XLSX.utils.book_new();
    const rcsaSheet = XLSX.utils.aoa_to_sheet(rcsaData);
  
    // Append sheets to the workbook
    XLSX.utils.book_append_sheet(workbook, rcsaSheet, 'RCSA');
  
    // Write the workbook to a file
    XLSX.writeFile(workbook, "Bank's Self Assessment.xlsx");
  };
  
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
        try {
            const response = await uploadExcelForRCSA(file);
            toast.success(response.message || "Excel file imported successfully");
  
            
        } catch (error) {
            toast.error("Error importing Excel file");
        }
    }
  };
  

  return (
    <div className="root">
      <Breadcrumb>
        <BreadcrumbItem>{t("header.you_are_here")}</BreadcrumbItem>
        <BreadcrumbItem active style={{ paddingRight: "10px" }}>
          {t("Risk.RCSA")}
        </BreadcrumbItem>
      </Breadcrumb>
      
      <div className="d-flex justify-content-between align-items-center mb-lg">
      <h5 className="mb-lg">{t("Risk.RCSA")}</h5>
      <div>
      <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleImportExcel}
            style={{ display: 'none' }}
            id="import-excel"
          />
          <b><label htmlFor="import-excel" className="btn submit-button2 mr-2 mt-2">
          <b><TfiImport className="mr-2 mb-2 font-weight-bold" size={12}/> {t("References_Page.import_excel")}</b>
          </label></b>

          <button className="btn submit-button mr-2" onClick={handleDownloadTemplate} style={{width: "250px",padding: "5px"}}>
          <b><CiExport className="mr-2 font-weight-bold" size={16}/>  {t("References_Page.download_excel_template")}</b>
          </button>
         
        </div>
        </div>
      {!notAllowed && <div
        className="container_info"
        style={{ padding: "40px 20px", backgroundColor: "var(--widget-bg)" }}
      >
        <div className="d-flex flex-wrap mb-3">
          <div className="flex-grow-1" style={{ flexBasis: 'calc(25% - 10px)', margin: '5px' }}>
            <Select
              options={quarterOptions}
              styles={customSelectStyles}
              placeholder="Select Quarter"
              onChange={(option: any) => setSelectedQuarter(option?.value || '')}
            />
          </div>
          <div className="flex-grow-1" style={{ flexBasis: 'calc(25% - 10px)', margin: '5px' }}>
            <Select
              options={yearOptions}
              styles={customSelectStyles}
              placeholder="Select Year"
              onChange={(option: any) => setSelectedYear(option?.value || 0)}
            />
          </div>
          
          <div className="flex-grow-1" style={{ flexBasis: 'calc(20% - 10px)', margin: '10px' }}>
            <button className="submit-button5 mr-2 ml-2" onClick={filterData}><CiFilter /> Filter</button>

            <button className="submit-button5  mr-2 ml-2" onClick={exportToExcel}> <CiExport /> Export to Excel</button>
          </div>
        </div>

        <div>
          <table className="styled-table-rcsa text-center">
            <thead style={{ backgroundColor: "#000000", color: "#ffffff" }}>
              <tr style={{ backgroundColor: "#000000", color: "#ffffff" }}>
                <th>{t("Function")}</th>
                <th>{t("Domain")}</th>
                <th>{t("Question")}</th>
                <th>{t("Bank Response")}</th>
                <th>{t("Control Type")}</th>
                <th>{t("Examples of Required Samples")}</th>
                <th>{t("Control Mapping")}</th>
                <th>{t("Bank Compliance Brief / Justification")}</th>
                <th>{t("Impacted/Affected Area")}</th>
                <th>{t("Owner")}</th>
              </tr>
            </thead>
            <tbody>

              {data.length > 0 ? (
                data?.map((risk: any, index: any) => (
                  <tr key={index}>
                    <td style={{ width: "15%" }}>{risk.function}</td>
                    <td style={{ width: "15%" }}>{risk.domain}</td>
                    <td style={{ width: "30%", textAlign: "left" }}>{risk.question}</td>
                    <td style={{ width: "10%", textAlign: "center" }}>{risk.bankResponse}</td>
                    <td style={{
                      width: "5%", textAlign: "center", backgroundColor: risk.controlType == 'Mandatory' ? "#dc4a4a" : risk.controlType == "Advisory" ? "#e8c412" : "#ffffff", color: risk.controlType == 'Mandatory' ? "#ffffff" : risk.controlType == "Advisory" ? "#000000" : "#000000"
                    }}>{risk.controlType}</td>
                    <td style={{ width: "10%", textAlign: "left" }}>{risk.evidence}</td>
                    <td style={{ width: "20%", textAlign: "left" }}>{risk.controlMapping}</td>
                    <td style={{ width: "20%", textAlign: "left" }}>{risk.complianceBrief}</td>
                    <td style={{ width: "15%" }}>{risk.impactedArea}</td>
                    <td style={{ width: "10%" }}>{risk.owner}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} style={{ backgroundColor: "#ffffff", color: "#000000" }} className="text-center">
                    {t("No data available.")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="pagination-controls mt-3">
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() => setPage(0)}
            disabled={page === 0}
          >
            {"<<"}
          </button>{" "}
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            disabled={page === 0}
          >
            {"<"}
          </button>{" "}
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() =>
              setPage((prev) =>
                Math.min(
                  prev + 1,
                  totalPages - 1
                )
              )
            }
            disabled={
              page >= totalPages - 1
            }
          >
            {">"}
          </button>{" "}
          <button
            className="btn btn-warning btn-sm text-white"
            onClick={() =>
              setPage(
                totalPages - 1
              )
            }
            disabled={
              page >= totalPages - 1
            }
          >
            {">>"}
          </button>{" "}
          <span>
            {t("question.page")}
            {page + 1} {t("question.to")} {totalPages}
          </span>
        </div>

      </div>}

      {notAllowed && <div className="container_info">
        <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
          <h5 style={{ width: '100%' }} className="text-center">{t("permissions.not_allowed")}</h5>
        </div>
      </div>}

    </div>
  );
}
